// ── M11: Demo Service ────────────────────────────────────────
// Mock storage, seed data, demo mode switching
// Mirrors real Supabase service interface using localStorage

import type { Task, TaskUpdate, Relationship, Profile, SpiceLevel } from '@/types'
import type { TaskFormData, TaskFilters } from '@/types/task'
import type { AuthUser } from '@/types/auth'
import { isDemoMode_M12_4, DEMO_ACCOUNTS, DEMO_PASSWORD } from '@/lib/constants'

// ── Storage Keys ──

const STORAGE_KEYS = {
  profiles: 'kinkup_demo_profiles',
  relationships: 'kinkup_demo_relationships',
  tasks: 'kinkup_demo_tasks',
  initialized: 'kinkup_demo_initialized',
} as const

// ── Helpers ──

function getStore<T>(key: string): T[] {
  try {
    const raw = localStorage.getItem(key)
    return raw ? (JSON.parse(raw) as T[]) : []
  } catch {
    return []
  }
}

function setStore<T>(key: string, data: T[]): void {
  localStorage.setItem(key, JSON.stringify(data))
}

function generateId(): string {
  return `demo_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`
}

// ── Seed Data ──

const SEED_PROFILES: Profile[] = [
  { id: 'demo_sir', email: 'sir@demo.com', role: 'dom', display_name: 'Sir', is_switch_paid: false, pronouns: 'he/him', bio: 'Experienced dom', calendar_public: false, created_at: '2024-01-01T00:00:00Z', updated_at: '2024-01-01T00:00:00Z' },
  { id: 'demo_master', email: 'master@demo.com', role: 'dom', display_name: 'Master', is_switch_paid: false, pronouns: 'he/him', bio: 'Strict but fair', calendar_public: false, created_at: '2024-01-01T00:00:00Z', updated_at: '2024-01-01T00:00:00Z' },
  { id: 'demo_mistress', email: 'mistress@demo.com', role: 'dom', display_name: 'Mistress', is_switch_paid: false, pronouns: 'she/her', bio: 'Creative and demanding', calendar_public: false, created_at: '2024-01-01T00:00:00Z', updated_at: '2024-01-01T00:00:00Z' },
  { id: 'demo_kitten', email: 'kitten@demo.com', role: 'sub', display_name: 'Kitten', is_switch_paid: false, pronouns: 'she/her', bio: 'Eager to please', calendar_public: true, created_at: '2024-01-01T00:00:00Z', updated_at: '2024-01-01T00:00:00Z' },
  { id: 'demo_princess', email: 'princess@demo.com', role: 'sub', display_name: 'Princess', is_switch_paid: false, pronouns: 'she/her', bio: 'Bratty but lovable', calendar_public: false, created_at: '2024-01-01T00:00:00Z', updated_at: '2024-01-01T00:00:00Z' },
  { id: 'demo_emma', email: 'emma@demo.com', role: 'sub', display_name: 'Emma', is_switch_paid: false, pronouns: 'she/her', bio: 'New and curious', calendar_public: true, created_at: '2024-01-01T00:00:00Z', updated_at: '2024-01-01T00:00:00Z' },
]

const SEED_RELATIONSHIPS: Relationship[] = [
  { id: 'rel_1', dom_id: 'demo_sir', sub_id: 'demo_kitten', status: 'active', initiated_by: 'demo_sir', created_at: '2024-01-15T00:00:00Z' },
  { id: 'rel_2', dom_id: 'demo_sir', sub_id: 'demo_princess', status: 'active', initiated_by: 'demo_sir', created_at: '2024-02-01T00:00:00Z' },
  { id: 'rel_3', dom_id: 'demo_master', sub_id: 'demo_emma', status: 'active', initiated_by: 'demo_master', created_at: '2024-02-15T00:00:00Z' },
  { id: 'rel_4', dom_id: 'demo_mistress', sub_id: 'demo_kitten', status: 'pending', initiated_by: 'demo_mistress', created_at: '2024-03-01T00:00:00Z' },
]

const now = new Date()
const yesterday = new Date(now.getTime() - 86400000)
const tomorrow = new Date(now.getTime() + 86400000)
const nextWeek = new Date(now.getTime() + 7 * 86400000)

const SEED_TASKS: Task[] = [
  { id: 'task_1', relationship_id: 'rel_1', title: 'Morning stretch routine', description: '15 minutes of stretching and mindfulness', points: 10, spice_level: 1 as SpiceLevel, reward_icon: null, due_date: tomorrow.toISOString(), status: 'pending', proof_required: false, proof_url: null, frequency: 'daily', created_by: 'demo_sir', created_at: '2024-03-01T00:00:00Z', completed_at: null },
  { id: 'task_2', relationship_id: 'rel_1', title: 'Write in journal', description: 'Write about your feelings and experiences today', points: 15, spice_level: 1 as SpiceLevel, reward_icon: null, due_date: tomorrow.toISOString(), status: 'pending', proof_required: true, proof_url: null, frequency: 'daily', created_by: 'demo_sir', created_at: '2024-03-01T00:00:00Z', completed_at: null },
  { id: 'task_3', relationship_id: 'rel_1', title: 'Choose outfit for date night', description: 'Pick something that makes you feel confident', points: 25, spice_level: 3 as SpiceLevel, reward_icon: null, due_date: nextWeek.toISOString(), status: 'pending', proof_required: true, proof_url: null, frequency: 'once', created_by: 'demo_sir', created_at: '2024-03-05T00:00:00Z', completed_at: null },
  { id: 'task_4', relationship_id: 'rel_1', title: 'Practice kneeling position', description: '10 minutes of proper form practice', points: 20, spice_level: 4 as SpiceLevel, reward_icon: null, due_date: yesterday.toISOString(), status: 'completed', proof_required: false, proof_url: null, frequency: 'weekly', created_by: 'demo_sir', created_at: '2024-02-28T00:00:00Z', completed_at: yesterday.toISOString() },
  { id: 'task_5', relationship_id: 'rel_2', title: 'Bedtime routine check-in', description: 'Report your bedtime routine completion', points: 10, spice_level: 1 as SpiceLevel, reward_icon: null, due_date: tomorrow.toISOString(), status: 'pending', proof_required: false, proof_url: null, frequency: 'daily', created_by: 'demo_sir', created_at: '2024-03-01T00:00:00Z', completed_at: null },
  { id: 'task_6', relationship_id: 'rel_2', title: 'Learn a new skill', description: 'Spend 30 minutes learning something new', points: 50, spice_level: 2 as SpiceLevel, reward_icon: null, due_date: nextWeek.toISOString(), status: 'verified', proof_required: true, proof_url: null, frequency: 'weekly', created_by: 'demo_sir', created_at: '2024-02-20T00:00:00Z', completed_at: '2024-02-25T00:00:00Z' },
  { id: 'task_7', relationship_id: 'rel_3', title: 'Morning meditation', description: '10 minutes of guided meditation', points: 10, spice_level: 1 as SpiceLevel, reward_icon: null, due_date: tomorrow.toISOString(), status: 'pending', proof_required: false, proof_url: null, frequency: 'daily', created_by: 'demo_master', created_at: '2024-03-01T00:00:00Z', completed_at: null },
  { id: 'task_8', relationship_id: 'rel_3', title: 'Write a poem', description: 'Express yourself through creative writing', points: 30, spice_level: 2 as SpiceLevel, reward_icon: null, due_date: nextWeek.toISOString(), status: 'completed', proof_required: true, proof_url: null, frequency: 'once', created_by: 'demo_master', created_at: '2024-03-02T00:00:00Z', completed_at: now.toISOString() },
]

// ── Public API ──

/** M11_1: Initialize localStorage with demo seed data */
export function initDemoData_M11_1(): void {
  if (localStorage.getItem(STORAGE_KEYS.initialized)) return

  setStore(STORAGE_KEYS.profiles, SEED_PROFILES)
  setStore(STORAGE_KEYS.relationships, SEED_RELATIONSHIPS)
  setStore(STORAGE_KEYS.tasks, SEED_TASKS)
  localStorage.setItem(STORAGE_KEYS.initialized, 'true')
}

/** M11_2: Check if app is in demo mode */
export function isDemoMode_M11_2(): boolean {
  return isDemoMode_M12_4()
}

/** M11_3: Authenticate a demo user */
export function getDemoUser_M11_3(email: string, password: string): AuthUser | null {
  if (password !== DEMO_PASSWORD) return null

  const account = Object.values(DEMO_ACCOUNTS).find((a) => a.email === email)
  if (!account) return null

  const profiles = getStore<Profile>(STORAGE_KEYS.profiles)
  const profile = profiles.find((p) => p.email === email) ?? null

  return {
    id: profile?.id ?? generateId(),
    email: account.email,
    role: account.role,
    displayName: account.displayName,
    profile,
    isDemo: true,
  }
}

/** M11_4: Reset demo data to initial seed state */
export function resetDemoData_M11_4(): void {
  localStorage.removeItem(STORAGE_KEYS.initialized)
  localStorage.removeItem(STORAGE_KEYS.profiles)
  localStorage.removeItem(STORAGE_KEYS.relationships)
  localStorage.removeItem(STORAGE_KEYS.tasks)
  initDemoData_M11_1()
}

/** M11_5: Fetch tasks from demo localStorage */
export function demoFetchTasks_M11_5(userId: string, filters?: TaskFilters): Task[] {
  const tasks = getStore<Task>(STORAGE_KEYS.tasks)
  const relationships = getStore<Relationship>(STORAGE_KEYS.relationships)

  // Filter to tasks in user's relationships
  const userRelIds = new Set(
    relationships
      .filter((r) => r.dom_id === userId || r.sub_id === userId)
      .map((r) => r.id)
  )

  let filtered = userId ? tasks.filter((t) => userRelIds.has(t.relationship_id)) : tasks

  if (filters?.status && filters.status !== 'all') {
    filtered = filtered.filter((t) => t.status === filters.status)
  }
  if (filters?.relationshipId) {
    filtered = filtered.filter((t) => t.relationship_id === filters.relationshipId)
  }

  return filtered.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
}

/** M11_6: Fetch relationships from demo localStorage */
export function demoFetchRelationships_M11_6(userId: string): Relationship[] {
  const relationships = getStore<Relationship>(STORAGE_KEYS.relationships)
  return relationships.filter(
    (r) => (r.dom_id === userId || r.sub_id === userId) && r.status !== 'ended'
  )
}

/** M11_7: Fetch profile from demo localStorage */
export function demoFetchProfile_M11_7(userId: string): Profile | null {
  const profiles = getStore<Profile>(STORAGE_KEYS.profiles)
  return profiles.find((p) => p.id === userId) ?? null
}

/** M11_8: Create task in demo localStorage */
export function demoCreateTask_M11_8(formData: TaskFormData): Task {
  const tasks = getStore<Task>(STORAGE_KEYS.tasks)

  const newTask: Task = {
    id: generateId(),
    relationship_id: formData.relationshipId,
    title: formData.title,
    description: formData.description || null,
    points: formData.points,
    spice_level: formData.spiceLevel,
    reward_icon: null,
    due_date: formData.dueDate,
    status: 'pending',
    proof_required: formData.proofRequired,
    proof_url: null,
    frequency: formData.frequency,
    created_by: '', // filled by caller context
    created_at: new Date().toISOString(),
    completed_at: null,
  }

  tasks.unshift(newTask)
  setStore(STORAGE_KEYS.tasks, tasks)
  return newTask
}

/** M11_9: Update task in demo localStorage */
export function demoUpdateTask_M11_9(taskId: string, updates: TaskUpdate): Task {
  const tasks = getStore<Task>(STORAGE_KEYS.tasks)
  const index = tasks.findIndex((t) => t.id === taskId)
  if (index === -1) throw new Error(`Task ${taskId} not found in demo data`)

  tasks[index] = { ...tasks[index], ...updates }
  setStore(STORAGE_KEYS.tasks, tasks)
  return tasks[index]
}
